// V4 — Mobile-first Dashboard. Replaces the desktop layout when the
// version switcher is on v4. Same data shape as Dashboard.jsx but
// every section is re-styled for a phone-shaped column:
//   • Compact greeting + team name
//   • Featured "Up next" match card (full-bleed)
//   • Inline action chips for pending availability (single tap)
//   • Captain's desk surfaces only the top-priority task per row
//   • Horizontal-scroll stat strip (instead of a 4-grid)
//   • Announcements as scannable cards

import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import {
  CalendarDays, Clock, MapPin, Trophy, Users, Wine, Target,
  Check, AlertTriangle, ChevronRight, Megaphone, Sparkles,
  Send, FileEdit, Award, Zap, Sigma, Hand, Shield, BarChart3, Activity
} from "lucide-react";
import { useActiveTeam } from "../components/ActiveTeam";
import { useToast } from "../components/Toast";
import { useTheme } from "../components/Theme";
import { api } from "../api";

const ELEVATED = ["ADMIN","PRESIDENT","VICE_PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"];
const STATUS_OPTIONS = [
  { value: "Available",     label: "Yes",   color: "var(--green)" },
  { value: "Tentative",     label: "Maybe", color: "var(--orange)" },
  { value: "Not Available", label: "No",    color: "var(--red)" }
];

function prettyDay(iso) {
  if (!iso) return "TBD";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function prettyDayLong(iso) {
  if (!iso) return "TBD";
  const d = new Date(`${iso}T00:00:00`);
  const today = new Date(); today.setHours(0,0,0,0);
  const matchD = new Date(`${iso}T00:00:00`);
  const dayDiff = Math.round((matchD - today) / (1000 * 60 * 60 * 24));
  if (dayDiff === 0) return "TODAY";
  if (dayDiff === 1) return "TOMORROW";
  if (dayDiff > 0 && dayDiff < 7) return d.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}

export default function DashboardMobile({ state, user, navigate, reload }) {
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const { theme } = useTheme();
  const elevated = user?.roles?.some(r => ELEVATED.includes(r));
  const today = new Date().toISOString().slice(0, 10);

  // Inline theme-aware palette for the greeting + section dividers.
  // Defined here (not in CSS) because earlier !important rules in
  // styles.css kept tying with the page-level h1 rules and rendering
  // unpredictably across deploys. Inline styles win over every CSS
  // rule, so this guarantees visibility regardless of cascade order.
  const T = theme === "light"
    ? { headline: "#0d1426", body: "#3a455e", divider: "#7a4d00", eyebrow: "#b07d05" }
    : { headline: "#ffffff", body: "#cfd6ee", divider: "#f7c948", eyebrow: "#ffd770" };

  // ---- Same scoped data computations as Dashboard.jsx ----
  const scopedMatches = useMemo(
    () => isAllTeams ? state.matches : state.matches.filter(m => m.teamId === activeTeamId),
    [state.matches, isAllTeams, activeTeamId]
  );
  const scopedMembers = useMemo(() => {
    if (isAllTeams) return state.members;
    const ids = new Set(activeTeam?.players || []);
    return state.members.filter(m => ids.has(m.id));
  }, [state.members, activeTeam, isAllTeams]);
  const scopedSquads = useMemo(() => {
    if (isAllTeams) return state.squads;
    const matchByTeam = new Map(state.matches.map(m => [m.id, m.teamId]));
    return state.squads.filter(s => matchByTeam.get(s.matchId) === activeTeamId);
  }, [state.squads, state.matches, isAllTeams, activeTeamId]);

  const upcoming = useMemo(
    () => scopedMatches.filter(m => m.date >= today)
      .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)),
    [scopedMatches, today]
  );
  const nextMatch = upcoming[0] || null;
  const next2Matches = upcoming.slice(0, 2);

  const pendingAvailability = useMemo(() => {
    if (!user?.id) return [];
    return next2Matches.filter(m => {
      const team = state.teams.find(t => t.id === m.teamId);
      if (!team?.players?.includes(user.id)) return false;
      return !state.availability.some(a => a.matchId === m.id && a.userId === user.id);
    });
  }, [next2Matches, state.teams, state.availability, user?.id]);

  async function quickSetAvailability(matchId, status) {
    try {
      await api.setAvailability(matchId, user.id, { status, comments: "" });
      await reload();
      toast.success(`Marked ${status.toLowerCase()}.`);
    } catch (err) { toast.error(err.message || "Couldn't update."); }
  }
  async function quickVolunteerDrinks(matchId) {
    try {
      await api.setAvailability(matchId, user.id, { volunteerDrinks: true });
      await reload();
      toast.success("Thanks for volunteering!");
    } catch (err) { toast.error(err.message || "Couldn't update."); }
  }

  const drinksDoneByUser = useMemo(() => {
    const s = new Set();
    for (const a of (state.availability || [])) if (a.volunteerDrinks) s.add(a.userId);
    return s;
  }, [state.availability]);
  const drinksAskMatch = useMemo(() => {
    if (!nextMatch || !user?.id) return null;
    if (drinksDoneByUser.has(user.id)) return null;
    const team = state.teams.find(t => t.id === nextMatch.teamId);
    if (!team?.players?.includes(user.id)) return null;
    const hasVol = state.availability.some(a => a.matchId === nextMatch.id && a.volunteerDrinks);
    return hasVol ? null : nextMatch;
  }, [nextMatch, drinksDoneByUser, user?.id, state.teams, state.availability]);

  const matchesNeedingSquad = useMemo(() => {
    if (!elevated) return [];
    const horizon = new Date(); horizon.setDate(horizon.getDate() + 21);
    const horIso = horizon.toISOString().slice(0, 10);
    const haveSquad = new Set(scopedSquads.map(s => s.matchId));
    return scopedMatches.filter(m =>
      m.date >= today && m.date <= horIso && !haveSquad.has(m.id)
    ).slice(0, 3);
  }, [elevated, scopedMatches, scopedSquads, today]);

  const draftsToPublish = useMemo(() => {
    if (!elevated) return [];
    return scopedSquads
      .filter(s => !s.isPublished)
      .map(s => ({ squad: s, match: scopedMatches.find(m => m.id === s.matchId) }))
      .filter(x => x.match && x.match.date >= today)
      .slice(0, 3);
  }, [elevated, scopedSquads, scopedMatches, today]);

  const noResponsePlayers = useMemo(() => {
    if (!elevated || !nextMatch) return { match: null, players: [] };
    const team = state.teams.find(t => t.id === nextMatch.teamId);
    if (!team) return { match: null, players: [] };
    const responded = new Set(
      state.availability.filter(a => a.matchId === nextMatch.id).map(a => a.userId)
    );
    const players = (team.players || [])
      .filter(uid => !responded.has(uid))
      .map(uid => state.members.find(m => m.id === uid))
      .filter(Boolean);
    return { match: nextMatch, players };
  }, [elevated, nextMatch, state.teams, state.availability, state.members]);

  const availabilityCounts = useMemo(() => {
    if (!nextMatch) return { a: 0, t: 0, n: 0, none: 0 };
    const team = state.teams.find(t => t.id === nextMatch.teamId);
    const total = (team?.players || []).length;
    const rows = state.availability.filter(a => a.matchId === nextMatch.id);
    const a = rows.filter(r => r.status === "Available").length;
    const t = rows.filter(r => r.status === "Tentative").length;
    const n = rows.filter(r => r.status === "Not Available").length;
    return { a, t, n, none: total - a - t - n };
  }, [nextMatch, state.teams, state.availability]);

  const ann = useMemo(
    () => [...state.announcements]
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
      .slice(0, 3),
    [state.announcements]
  );

  const hasCaptainTasks = matchesNeedingSquad.length + draftsToPublish.length + (noResponsePlayers.match ? 1 : 0) > 0;

  // ────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────
  return (
    <div className="m-dash">

      {/* ─── Greeting ──────────────────────────────────────── */}
      <header className="m-dash-hello">
        <small style={{ color: T.eyebrow }}>
          {isAllTeams ? "All Teams" : (activeTeam?.name || "Pitt Gabruz")}
        </small>
        <h1 style={{ color: T.headline }}>
          Hi, {user.name?.split(" ")[0] || user.name || "there"}
        </h1>
        <p style={{ color: T.body }}>
          {pendingAvailability.length > 0
            ? `${pendingAvailability.length} match${pendingAvailability.length > 1 ? "es" : ""} need${pendingAvailability.length === 1 ? "s" : ""} your RSVP.`
            : (nextMatch
                ? `Next up — ${nextMatch.opponent} ${prettyDay(nextMatch.date).toLowerCase()}.`
                : "No matches scheduled.")}
        </p>
      </header>

      {/* ─── Featured "Up Next" match card ──────────────────── */}
      {nextMatch && (
        <section className="m-dash-section">
          <h3 className="m-dash-h3" style={{color: T.divider}}>Up Next</h3>
          <article className="m-dash-matchHero" onClick={() => navigate?.("schedule")}>
            <div className="m-dash-matchHero-day">
              {prettyDayLong(nextMatch.date)}
              {nextMatch.time && <em> · {nextMatch.time}</em>}
            </div>
            <div className="m-dash-matchHero-vs">
              <div>
                <small>HOME</small>
                <b>{isAllTeams ? "Pitt Gabruz" : (activeTeam?.name?.replace(/(Cricket|Volleyball|Team|Club)/gi, "").trim() || "Pitt Gabruz")}</b>
              </div>
              <span className="m-dash-matchHero-vsLabel">VS</span>
              <div>
                <small>AWAY</small>
                <b>{nextMatch.opponent}</b>
              </div>
            </div>
            {nextMatch.venue && (
              <div className="m-dash-matchHero-venue">
                <MapPin size={13}/> {nextMatch.venue}
              </div>
            )}
            <div className="m-dash-matchHero-counts">
              <span className="m-dash-pill m-dash-pill--green">{availabilityCounts.a} Yes</span>
              <span className="m-dash-pill m-dash-pill--amber">{availabilityCounts.t} Maybe</span>
              <span className="m-dash-pill m-dash-pill--red">{availabilityCounts.n} No</span>
              <span className="m-dash-pill m-dash-pill--gray">{availabilityCounts.none} Pending</span>
            </div>
            <div className="m-dash-matchHero-cta">
              <button type="button" className="dark" onClick={e => { e.stopPropagation(); navigate?.("availability"); }}>
                <CalendarDays size={14}/> Availability
              </button>
              <button type="button" className="btn" onClick={e => { e.stopPropagation(); navigate?.("squad"); }}>
                <Trophy size={14}/> Squad <ChevronRight size={14}/>
              </button>
            </div>
          </article>
        </section>
      )}

      {/* ─── Your Actions ──────────────────────────────────── */}
      {(pendingAvailability.length > 0 || drinksAskMatch) && (
        <section className="m-dash-section">
          <h3 className="m-dash-h3" style={{color: T.divider}}>
            <AlertTriangle size={14} style={{color:"var(--orange)"}}/> Action needed
          </h3>
          <div className="m-dash-actionList">
            {pendingAvailability.map(m => (
              <article key={`av-${m.id}`} className="m-dash-actionCard">
                <div className="m-dash-actionCard-head">
                  <div>
                    <small>RSVP</small>
                    <strong>vs {m.opponent}</strong>
                    <em>{prettyDay(m.date)} · {m.time || "TBD"}</em>
                  </div>
                </div>
                <div className="m-dash-rsvpBtns">
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className="m-dash-rsvpBtn"
                      style={{ borderColor: opt.color, color: opt.color }}
                      onClick={() => quickSetAvailability(m.id, opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </article>
            ))}
            {drinksAskMatch && (
              <article className="m-dash-actionCard">
                <div className="m-dash-actionCard-head">
                  <div>
                    <small>VOLUNTEER</small>
                    <strong>Drinks · vs {drinksAskMatch.opponent}</strong>
                    <em>No one has signed up. Take a turn this season.</em>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn"
                  style={{width:"100%"}}
                  onClick={() => quickVolunteerDrinks(drinksAskMatch.id)}
                ><Wine size={14}/> Volunteer</button>
              </article>
            )}
          </div>
        </section>
      )}

      {/* "All caught up" state */}
      {pendingAvailability.length === 0 && !drinksAskMatch && (
        <section className="m-dash-section">
          <div className="m-dash-empty">
            <Check size={28}/>
            <p>You're all caught up</p>
          </div>
        </section>
      )}

      {/* ─── Captain's desk ────────────────────────────────── */}
      {elevated && hasCaptainTasks && (
        <section className="m-dash-section">
          <h3 className="m-dash-h3" style={{color: T.divider}}>
            <Sparkles size={14} style={{color:"var(--gold)"}}/> Captain's desk
          </h3>
          <div className="m-dash-actionList">
            {matchesNeedingSquad.map(m => (
              <article key={`needSquad-${m.id}`} className="m-dash-actionCard">
                <div className="m-dash-actionCard-head">
                  <Trophy size={18} style={{color:"var(--orange)", flexShrink:0}}/>
                  <div>
                    <small>BUILD SQUAD</small>
                    <strong>vs {m.opponent}</strong>
                    <em>{prettyDay(m.date)} · {m.sport}</em>
                  </div>
                </div>
                <button type="button" className="dark m-dash-cardCta" onClick={() => navigate?.("squad")}>
                  Open Squads <ChevronRight size={14}/>
                </button>
              </article>
            ))}
            {draftsToPublish.map(({ squad, match }) => (
              <article key={`draft-${squad.id}`} className="m-dash-actionCard">
                <div className="m-dash-actionCard-head">
                  <FileEdit size={18} style={{color:"var(--blue, #5ce1e6)", flexShrink:0}}/>
                  <div>
                    <small>DRAFT READY</small>
                    <strong>vs {match.opponent}</strong>
                    <em>{squad.players?.length || 0} starters · publish to announce</em>
                  </div>
                </div>
                <button type="button" className="btn m-dash-cardCta" onClick={() => navigate?.("squad")}>
                  <Send size={14}/> Publish
                </button>
              </article>
            ))}
            {noResponsePlayers.match && noResponsePlayers.players.length > 0 && (
              <article className="m-dash-actionCard">
                <div className="m-dash-actionCard-head">
                  <Users size={18} style={{color:"var(--muted)", flexShrink:0}}/>
                  <div>
                    <small>NO RESPONSE</small>
                    <strong>{noResponsePlayers.players.length} unresponsive</strong>
                    <em>{noResponsePlayers.players.slice(0,3).map(p => p.name).join(", ")}{noResponsePlayers.players.length > 3 ? `, +${noResponsePlayers.players.length - 3} more` : ""}</em>
                  </div>
                </div>
                <button type="button" className="dark m-dash-cardCta" onClick={() => navigate?.("availability")}>
                  Open Availability <ChevronRight size={14}/>
                </button>
              </article>
            )}
          </div>
        </section>
      )}

      {/* ─── My Cricket Stats (Rowdys only) ────────────────── */}
      {activeTeam?.code === "ROWDYS_CRICKET" && (
        <MyCricketStatsMobile user={user} team={activeTeam}/>
      )}

      {/* ─── At a glance — horizontal scroll strip ─────────── */}
      <section className="m-dash-section">
        <h3 className="m-dash-h3" style={{color: T.divider}}>At a glance</h3>
        <div className="m-dash-statStrip">
          <MiniCard
            icon={Users}
            color="#5ce1e6"
            value={scopedMembers.length}
            label={isAllTeams ? "Members" : "Team"}
          />
          <MiniCard
            icon={CalendarDays}
            color="var(--gold)"
            value={upcoming.length}
            label="Upcoming"
          />
          <MiniCard
            icon={Trophy}
            color="#ffd76a"
            value={scopedSquads.length}
            label="Squads"
          />
          <MiniCard
            icon={Check}
            color="var(--green)"
            value={`${availabilityCounts.a}/${(activeTeam?.players?.length || 0)}`}
            label="Avail next"
          />
        </div>
      </section>

      {/* ─── Latest announcements ──────────────────────────── */}
      {ann.length > 0 && (
        <section className="m-dash-section">
          <h3 className="m-dash-h3" style={{color: T.divider}}>
            <Megaphone size={14}/> Announcements
          </h3>
          <div className="m-dash-annList">
            {ann.map(a => (
              <article key={a.id} className="m-dash-annCard">
                <header>
                  <strong>{a.title}</strong>
                  <span className={`m-dash-priority m-dash-priority--${(a.priority || "low").toLowerCase()}`}>
                    {a.priority}
                  </span>
                </header>
                <p>{a.body?.slice(0, 120)}{(a.body?.length || 0) > 120 ? "…" : ""}</p>
                <small>by {a.postedBy}</small>
              </article>
            ))}
          </div>
        </section>
      )}

    </div>
  );
}

// Horizontal-scroll mini stat card
function MiniCard({ icon: Icon, color, value, label }) {
  return (
    <div className="m-dash-mini" style={{ "--accent": color }}>
      <Icon size={18}/>
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// MyCricketStatsMobile — phone-friendly version of the desktop
// MyCricketStats panel. Same data, condensed layout.
// ──────────────────────────────────────────────────────────────────
const HEADLINE_FIELDS = [
  { key: "matchesPlayed", label: "Matches",  icon: Trophy,  accent: "var(--gold)" },
  { key: "runsScored",    label: "Runs",     icon: Target,  accent: "#5ce1e6" },
  { key: "wicketsTaken",  label: "Wickets",  icon: Shield,  accent: "#ff8a2a" },
  { key: "catchesTaken",  label: "Catches",  icon: Hand,    accent: "#c897ff" },
  { key: "centuries",     label: "100's",    icon: Award,   accent: "#ffd76a" },
  { key: "sixes",         label: "6's",      icon: Zap,     accent: "#5ce1e6" },
  { key: "fours",         label: "4's",      icon: Sigma,   accent: "#c897ff" },
  { key: "maidenOvers",   label: "Maidens",  icon: Activity, accent: "#ffd76a" }
];
function customIcon(name) { return Icons[name] || BarChart3; }

function MyCricketStatsMobile({ user, team }) {
  const [stats, setStats]   = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.getPlayerStats(user.id, team.id),
      api.listCricketStatFields()
    ])
      .then(([row, fdefs]) => {
        if (cancelled) return;
        setStats(row);
        setFields(fdefs);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.id, team.id]);

  if (loading) return null;

  const matches = stats?.matchesPlayed || 0;
  const runs    = stats?.runsScored    || 0;
  const wickets = stats?.wicketsTaken  || 0;
  const battingAvg = matches > 0 ? (runs / matches).toFixed(1) : "—";
  const wpm        = matches > 0 ? (wickets / matches).toFixed(2) : "—";

  return (
    <section className="m-dash-section">
      <h3 className="m-dash-h3" style={{color: T.divider}}>My career — {team.name}</h3>
      <article className="m-dash-statsHero">
        <div className="m-dash-statsHero-avgs">
          <div><small>BAT AVG</small><b>{battingAvg}</b></div>
          <div><small>WKT / MATCH</small><b>{wpm}</b></div>
        </div>
        <div className="m-dash-statsHero-grid">
          {HEADLINE_FIELDS.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.key} className="m-dash-statTile" style={{ "--accent": f.accent }}>
                <Icon size={16}/>
                <b>{stats?.[f.key] ?? 0}</b>
                <span>{f.label}</span>
              </div>
            );
          })}
          {fields.map(f => {
            const Icon = customIcon(f.icon);
            return (
              <div key={`c-${f.id}`} className="m-dash-statTile" style={{ "--accent": f.accent }}>
                <Icon size={16}/>
                <b>{stats?.custom?.[f.fieldKey] ?? 0}</b>
                <span>{f.label}</span>
              </div>
            );
          })}
        </div>
      </article>
    </section>
  );
}
